import { toolCategoriesQuery } from "@common/api/tool-categories"
import { toolsQuery } from "@common/api/tools"
import { Button } from "@common/components/ui/button"
import { DropdownMenuCheckboxItem } from "@common/components/ui/dropdown-menu"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { ToolTable } from "./-components/tool-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
  type ToolSort,
} from "./-lib/columns"
import {
  filterValuesOf,
  hasToolFilters,
  NO_TOOL_FILTERS,
  toolFilterConfigs,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS, type Grouping } from "./-lib/grouping"
import {
  displayOf,
  listParams,
  type ToolsDisplay,
  type ToolsSearch,
  toolsSearchSchema,
} from "./-lib/search"
import { inTab, needsAttention, TABS, type Tab, tabLabel } from "./-lib/views"

const COUNTED: readonly Tab[] = ["out", "calibration_due", "out_of_service"]

export const Route = createFileRoute("/_layout/tools")({
  staticData: { breadcrumb: "Tools" },
  validateSearch: (search) => toolsSearchSchema.parse(search),
  loaderDeps: ({ search }) => listParams(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "tools:read", "/"),
  loader: ({ context: { queryClient }, deps }) =>
    Promise.all([
      queryClient.ensureQueryData(toolsQuery(deps)),
      queryClient.ensureQueryData(toolCategoriesQuery()),
    ]),
  component: ToolsPage,
})

function useUpdateSearch(): (patch: Partial<ToolsSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function ToolsPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={
        <Toolbar display={display} hidden={fit.hidden} panelOpen={open} />
      }
      pane={open && !compact ? "split" : "closed"}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      <ToolTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() =>
          update({
            ...NO_TOOL_FILTERS,
            search: undefined,
            retired: undefined,
          })
        }
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
  panelOpen,
}: {
  display: ToolsDisplay
  hidden: Column[]
  panelOpen: boolean
}): React.ReactElement {
  const search = Route.useSearch()
  const { data } = useSuspenseQuery(toolsQuery(listParams(search)))
  const { tab = "all", retired = false } = search
  const update = useUpdateSearch()
  const canCreate = useHasPermission("tools:create")
  const live = data.data.filter((tool) => tool.status !== "retired")
  const tabs = panelOpen
    ? TABS.filter((value) => value === "all" || value === tab)
    : TABS
  const label = (value: Tab) => {
    const count = live.filter((tool) => inTab(tool, value)).length
    return COUNTED.includes(value) && count
      ? `${tabLabel(value)} ${count}`
      : tabLabel(value)
  }

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={tabs}
          tab={tab}
          label={label}
          marked={(value) => needsAttention(live, value)}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search.search ?? ""}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search tools…"
          />
          <FilterButton
            active={retired || hasToolFilters(filterValuesOf(search))}
            configs={toolFilterConfigs}
            onChange={update}
          >
            <DropdownMenuCheckboxItem
              checked={retired}
              onCheckedChange={(checked) =>
                update({ retired: checked === true || undefined })
              }
            >
              Show retired
            </DropdownMenuCheckboxItem>
          </FilterButton>
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["number"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/tools/new" search={true}>
                New tool
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<ToolsSearch> {
  return {
    group: group === "status" ? undefined : (group ?? "none"),
    subgroup,
  }
}

function sortSearch(
  display: ToolsDisplay,
  column: ToolSort,
): Partial<ToolsSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "number" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function columnsSearch(columns: Column[]): Partial<ToolsSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function ActiveFilters(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const values = filterValuesOf(search)

  return (
    <ActiveFilterBar
      active={hasToolFilters(values)}
      configs={toolFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_TOOL_FILTERS)}
    />
  )
}
